import { useState } from 'react'
import { useParams } from 'react-router-dom'

import { Box, Button, Spinner } from '@factorialco/deprecated-design-system'
import { AssignCompetenciesToRoleDocument } from 'generated/resources/graphql'
import { differenceBy } from 'lodash'

import Modal from 'modules/core/components/Modal'
import Selector from 'modules/core/components/Selector'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import { useCompetencies } from 'modules/job_catalog/hooks/useCompetencies'
import { useRoleCompetencies } from 'modules/job_catalog/hooks/useRoleCompetencies'

interface Props {
  onSuccess: () => void
  onClose: () => void
}

export const AssignCompetenciesModal = ({ onSuccess, onClose }: Props) => {
  const { id } = useParams<{ id: string }>()
  const roleId = Number(id)
  const [competencies, fetchingCompetencies] = useCompetencies()
  const [roleCompetencies, fetchingRoleCompetencies] = useRoleCompetencies(roleId)
  const [addCompetenciesToRole, { loading: isAddingCompetencies }] = useMutation(
    AssignCompetenciesToRoleDocument,
    {
      extractorFn: (data) => data.jobCatalogMutations.addCompetenciesJobcatalogRole,
    }
  )
  const [selectedCompetencyIds, setSelectedCompetencyIds] = useState<number[]>([])

  const availableCompetencies = differenceBy(
    competencies || [],
    roleCompetencies || [],
    (competency) => competency.id
  )

  const handleSubmit = async () => {
    track(trackingEvents.jobCatalog.jobRoleAssignCompetencySubmit)
    const result = await addCompetenciesToRole({
      variables: { id: roleId, competenciesIds: selectedCompetencyIds },
    })
    const handled = handleResult(result, () => {})

    if (handled.success) {
      onSuccess()
    }
  }

  const handleClose = () => {
    track(trackingEvents.jobCatalog.jobRoleAssignCompetencyCancel)
    onClose()
  }

  return (
    <Modal
      illustration="document-folder-board-edit"
      title={i18n.t('job_catalog.competencies.title')}
      description={i18n.t('job_catalog.competencies.assign_competencies_description')}
      onClose={handleClose}
      onSubmit={handleSubmit}
      footer={
        <Box flexDirection="row" gap="s8">
          <Button type="secondary" disabled={isAddingCompetencies} onClick={handleClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button disabled={!selectedCompetencyIds.length || isAddingCompetencies} submit>
            {i18n.t('common.submit')}
          </Button>
        </Box>
      }
    >
      {fetchingCompetencies || fetchingRoleCompetencies ? (
        <Box alignItems="center">
          <Spinner />
        </Box>
      ) : (
        <Selector.Container
          value={selectedCompetencyIds}
          searchPlaceholder={i18n.t('common.search')}
          blankLabel={i18n.t('job_catalog.competencies.no_competencies_found')}
          // eslint-disable-next-line no-type-assertion/no-type-assertion -- Selector component has no generics
          onChange={(x) => setSelectedCompetencyIds(x as number[])}
          isMultiSelect
        >
          {availableCompetencies.map((competency) => (
            <Selector.Option
              key={competency.id}
              value={competency.id}
              searchValue={competency.name}
              label={competency.name}
            >
              {competency.name}
            </Selector.Option>
          ))}
        </Selector.Container>
      )}
    </Modal>
  )
}
